import { useQuery } from "@tanstack/react-query";
import { mcpClient } from "../../lib/mcp-client";

/**
 * Sprint 17 — MCP endpoint topology. One entry per route Kuvio serves; mirrors
 * the `list_mcp_endpoints` tool response.
 */
export interface McpEndpoint {
  path: string;
  /** Absolute URL (origin + path) — what the user pastes into a host config. */
  url: string;
  label: string;
  description: string;
  tool_count: number;
  tool_scope: string;
  requires_role: "owner" | "member";
  /** ISO timestamp this session last hit the endpoint, or null. */
  last_seen: string | null;
  /**
   * Sprint 24.11 — pre-handle URL form(s) that still resolve to this per-source
   * endpoint (transparent backward compat). Present only for connection/wrapper rows.
   */
  legacy_aliases?: string[];
}

export interface McpEndpointCatalogue {
  base_url: string;
  endpoints: McpEndpoint[];
}

const EMPTY: McpEndpointCatalogue = { base_url: "", endpoints: [] };

/**
 * Fetch the endpoint catalogue via `list_mcp_endpoints`. TanStack-cached under a
 * stable key so the Settings → Endpoints page and the Cmd-K copy picker share one
 * fetch. Counts shift only when entity types change, so a 60s staleTime matches
 * the server-side cache.
 */
export function useMcpEndpoints() {
  return useQuery<McpEndpointCatalogue>({
    queryKey: ["mcp-endpoints"],
    queryFn: async () => {
      const result = await mcpClient.toolsCall<McpEndpointCatalogue>("list_mcp_endpoints");
      return (result.structuredContent ?? EMPTY) as McpEndpointCatalogue;
    },
    staleTime: 60_000,
  });
}
